import { useState } from 'react';
import { Camera, Eye, CheckSquare, Square, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { BrandfolderAsset } from '../../types/brandfolder';
import { getResizedImageUrl } from '../../utils/imageUrl';

interface DamAssetGridProps {
  assets: BrandfolderAsset[];
  loading: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  selectedAssets: Set<string>;
  isSelectionMode: boolean;
  onSelect: (assetId: string) => void;
  onPreview: (asset: BrandfolderAsset) => void;
  selectionLimit: number;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Selected':
      return 'bg-green-100 text-green-800';
    case 'Under Review':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const DamAssetCard: React.FC<{
  asset: BrandfolderAsset;
  selected: boolean;
  isSelectionMode: boolean;
  onSelect: (assetId: string) => void;
  onPreview: (asset: BrandfolderAsset) => void;
  selectionDisabled: boolean;
}> = ({ asset, selected, isSelectionMode, onSelect, onPreview, selectionDisabled }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const canBeSelected = asset.status === 'Under Review' && !selectionDisabled;

  const handleCardClick = () => {
    if (isSelectionMode && canBeSelected) {
      onSelect(asset.bf_asset_id);
    } else if (!isSelectionMode) {
      onPreview(asset);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canBeSelected) {
      onSelect(asset.bf_asset_id);
    }
  };

  const handleEyeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview(asset);
  };

  return (
    <div
      className={`
        group relative bg-white rounded-lg border-2 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg
        ${selected ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'}
        ${!canBeSelected && isSelectionMode ? 'opacity-50 cursor-default' : ''}
      `}
      onClick={handleCardClick}
    >
      {/* Image */}
      <div className="aspect-square relative overflow-hidden bg-gray-100">
        {!imageError ? (
          <>
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Camera className="h-8 w-8 text-gray-400 animate-pulse" />
              </div>
            )}
            <img
              src={getResizedImageUrl(asset.url, 300)}
              alt={`Brandfolder asset ${asset.bf_asset_id}`}
              referrerPolicy="no-referrer"
              className={`w-full h-full object-cover transition-opacity duration-200 ${
                imageLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
            />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Camera className="h-8 w-8 text-gray-400" />
          </div>
        )}

        {/* Hover overlay with Eye icon — opens detail view */}
        <div
          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center"
          onClick={handleEyeClick}
        >
          <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        </div>

        {/* Selection Checkbox */}
        {isSelectionMode && canBeSelected && (
          <div className="absolute top-2 left-2 z-10">
            <button
              onClick={handleCheckboxClick}
              className="p-1 bg-white rounded-md shadow-sm hover:bg-gray-50 transition-colors"
            >
              {selected ? (
                <CheckSquare className="h-4 w-4 text-blue-600" />
              ) : (
                <Square className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 right-2 z-10">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(asset.status)}`}>
            {asset.status}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-xs text-gray-500 truncate" title={asset.bf_asset_id}>
          {asset.bf_asset_id}
        </p>
      </div>
    </div>
  );
};

export const DamAssetGrid: React.FC<DamAssetGridProps> = ({
  assets,
  loading,
  hasNextPage,
  onLoadMore,
  selectedAssets,
  isSelectionMode,
  onSelect,
  onPreview,
  selectionLimit
}) => {
  if (loading && assets.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!loading && assets.length === 0) {
    return (
      <div className="text-center py-12">
        <Camera className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">No assets found in this label</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {assets.map((asset, index) => (
          <DamAssetCard
            key={asset.bf_asset_id || `dam-asset-${index}`}
            asset={asset}
            selected={selectedAssets.has(asset.bf_asset_id)}
            isSelectionMode={isSelectionMode}
            onSelect={onSelect}
            onPreview={onPreview}
            selectionDisabled={
              selectedAssets.size >= selectionLimit && !selectedAssets.has(asset.bf_asset_id)
            }
          />
        ))}
      </div>

      {/* Load More */}
      {hasNextPage && (
        <div className="mt-6 text-center">
          <Button
            variant="secondary"
            onClick={onLoadMore}
            disabled={loading}
            className="px-6"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading More...
              </>
            ) : (
              <>Load More ({assets.length} shown)</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
